import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BulkConfirmation } from '@/components/bulk/BulkConfirmation';
import { BulkFlowHeader } from '@/components/bulk/BulkFlowHeader';
import { CreateStep } from '@/components/bulk/CreateStep';
import { MotifsStep } from '@/components/bulk/MotifsStep';
import { ReferenceStep } from '@/components/bulk/ReferenceStep';
import { useBulkSubmit } from '@/components/bulk/useBulkSubmit';
import { useMotifUploads } from '@/components/bulk/useMotifUploads';
import { useDesignEntry } from '@/components/designs/DesignEntryChoice';
import { PageHeader } from '@/components/ui';
import { embedLayout } from '@/lib/embed-layout';
import { postEmbedMessage } from '@/lib/embed-messages';
import { useBulkCreationStore } from '@/stores/bulkCreationStore';

/**
 * Bulk Creation (`/designs/bulk`): many listings from one published
 * reference, in three steps. Rendered inside the SellerShell, which owns the
 * navigation.
 *
 * The page owns what must outlive a step switch: the upload runner (uploads
 * keep going while the seller looks at step 03) and the submission (its
 * answer still lands after a pill click). Once the server accepted the run
 * the flow state is cleared and only the confirmation remains.
 */
export function BulkCreationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentStep = useBulkCreationStore((state) => state.currentStep);
  const asDraft = useBulkCreationStore((state) => state.asDraft);
  const uploads = useMotifUploads();
  const [confirmedTotal, setConfirmedTotal] = useState<number | null>(null);
  const { startGuided } = useDesignEntry();
  const { hostChrome } = embedLayout();

  // Polaris embed only (lib/embed-messages.ts): a host that starts here
  // directly waits for this before it drops its loading state.
  useEffect(() => {
    postEmbedMessage({ type: 'designdeck:embed:ready' });
  }, []);

  const handleCreated = useCallback((totalProducts: number) => {
    useBulkCreationStore.getState().reset();
    setConfirmedTotal(totalProducts);
    postEmbedMessage({ type: 'designdeck:embed:bulk-created', totalProducts });
  }, []);
  const submit = useBulkSubmit({ onCreated: handleCreated });

  const total = submit.counts.totalProducts;
  const createAction = {
    label: asDraft
      ? t('seller.bulk.create.cta.draft', { count: total })
      : t('seller.bulk.create.cta.create', { count: total }),
    disabled: submit.block !== null,
    loading: submit.isSubmitting,
    loadingLabel: t('seller.bulk.create.cta.submitting'),
    onClick: submit.submit,
  };

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <PageHeader
        eyebrow={t('seller.bulk.eyebrow')}
        title={t('seller.bulk.title')}
        description={t('seller.bulk.intro')}
      />
      <div data-testid="bulk-creation-flow" className="flex flex-col gap-6">
        {confirmedTotal !== null ? (
          <BulkConfirmation
            totalProducts={confirmedTotal}
            hostChrome={hostChrome}
            onOpenOverview={() =>
              hostChrome ? startGuided() : navigate('/designs')
            }
            onBackToStart={() => {
              useBulkCreationStore.getState().reset();
              setConfirmedTotal(null);
            }}
          />
        ) : (
          <>
            <BulkFlowHeader
              createAction={createAction}
              busy={submit.isSubmitting}
            />
            {currentStep === 'reference' && <ReferenceStep />}
            {currentStep === 'motifs' && <MotifsStep uploads={uploads} />}
            {currentStep === 'create' && <CreateStep submit={submit} />}
          </>
        )}
      </div>
    </div>
  );
}
